const CONFIG_SELECTOR = 'script[data-ruffoo-smart-header-config]';
const DESKTOP_QUERY = '(min-width: 990px)';
const UPWARD_REVEAL_DISTANCE = 8;
const CONTROLLER_KEY = '__ruffooSmartHeaderController';
const STYLE_ID = 'ruffoo-smart-header-styles';

function installStyles() {
  if (document.getElementById(STYLE_ID)) return;

  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
    @media screen and (min-width: 990px) {
      #header-component[data-ruffoo-smart-header='true'] {
        transition: transform var(--ruffoo-smart-header-speed, 200ms) ease;
        will-change: transform;
      }

      #header-component[data-ruffoo-smart-header='true'] .header__row--top {
        overflow: hidden;
        max-height: var(--ruffoo-smart-header-top-height, 240px);
        opacity: 1;
        transform: translateY(0);
        transition:
          max-height var(--ruffoo-smart-header-speed, 200ms) ease,
          opacity var(--ruffoo-smart-header-speed, 200ms) ease,
          transform var(--ruffoo-smart-header-speed, 200ms) ease,
          border-bottom-width var(--ruffoo-smart-header-speed, 200ms) ease;
      }

      #header-component[data-ruffoo-smart-state='menu'] .header__row--top,
      #header-component[data-ruffoo-smart-state='hidden'] .header__row--top {
        max-height: 0;
        opacity: 0;
        transform: translateY(-8px);
        border-bottom-width: 0;
        pointer-events: none;
      }

      #header-component[data-ruffoo-smart-state='hidden'] {
        transform: translateY(calc(-100% - 2px));
        pointer-events: none;
      }
    }

    @media (prefers-reduced-motion: reduce) {
      #header-component[data-ruffoo-smart-header='true'],
      #header-component[data-ruffoo-smart-header='true'] .header__row--top {
        transition-duration: 0.01ms;
      }
    }
  `;
  document.head.append(style);
}

class RuffooSmartHeaderController {
  constructor(header, config) {
    this.header = header;
    this.config = config;
    this.topRow = header.querySelector('.header__row--top');
    this.mediaQuery = window.matchMedia(DESKTOP_QUERY);
    this.active = false;
    this.state = 'full';
    this.lastScrollTop = document.scrollingElement?.scrollTop ?? 0;
    this.downwardStart = this.lastScrollTop;
    this.upwardTravel = 0;
    this.frame = null;

    this.handleScroll = this.handleScroll.bind(this);
    this.handleViewportChange = this.handleViewportChange.bind(this);
    this.handleInteraction = this.handleInteraction.bind(this);
    this.measureTopRow = this.measureTopRow.bind(this);
  }

  init() {
    if (!this.config.enabled || !this.topRow) return;

    installStyles();
    this.header.dataset.ruffooSmartHeader = 'true';
    this.header.style.setProperty('--ruffoo-smart-header-speed', `${this.config.animationSpeed}ms`);

    this.resizeObserver = new ResizeObserver(this.measureTopRow);
    this.resizeObserver.observe(this.topRow);
    this.measureTopRow();

    document.addEventListener('scroll', this.handleScroll, { passive: true });
    this.header.addEventListener('mouseenter', this.handleInteraction);
    this.header.addEventListener('focusin', this.handleInteraction);
    this.mediaQuery.addEventListener('change', this.handleViewportChange);

    this.handleViewportChange();
  }

  destroy() {
    document.removeEventListener('scroll', this.handleScroll);
    this.header.removeEventListener('mouseenter', this.handleInteraction);
    this.header.removeEventListener('focusin', this.handleInteraction);
    this.mediaQuery.removeEventListener('change', this.handleViewportChange);
    this.resizeObserver?.disconnect();

    if (this.frame) cancelAnimationFrame(this.frame);

    delete this.header.dataset.ruffooSmartHeader;
    delete this.header.dataset.ruffooSmartState;
    this.header.style.removeProperty('--ruffoo-smart-header-speed');
    this.header.style.removeProperty('--ruffoo-smart-header-top-height');
  }

  measureTopRow() {
    if (!this.topRow) return;

    const height = Math.ceil(this.topRow.scrollHeight);
    if (height > 0) {
      this.header.style.setProperty('--ruffoo-smart-header-top-height', `${height}px`);
    }
  }

  handleViewportChange() {
    this.active = this.mediaQuery.matches;
    this.lastScrollTop = document.scrollingElement?.scrollTop ?? 0;
    this.downwardStart = this.lastScrollTop;
    this.upwardTravel = 0;

    if (!this.active) {
      delete this.header.dataset.ruffooSmartState;
      this.state = 'full';
      return;
    }

    this.measureTopRow();
    this.updateFromScroll(true);
  }

  handleScroll() {
    if (!this.active || this.frame) return;

    this.frame = requestAnimationFrame(() => {
      this.frame = null;
      this.updateFromScroll(false);
    });
  }

  handleInteraction() {
    if (!this.active) return;

    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    if (scrollTop <= 1 || this.header.getBoundingClientRect().top > 0) return;

    this.setState('menu');
    this.downwardStart = scrollTop;
    this.upwardTravel = 0;
  }

  updateFromScroll(force) {
    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    const delta = scrollTop - this.lastScrollTop;
    const isAtPageTop = scrollTop <= 1;
    const isSticky = this.header.dataset.stickyState === 'active' || this.header.getBoundingClientRect().top <= 0;

    if (isAtPageTop) {
      this.setState('full');
      this.resetTravel(scrollTop);
      this.lastScrollTop = scrollTop;
      return;
    }

    if (!isSticky) {
      this.setState('full');
      this.resetTravel(scrollTop);
      this.lastScrollTop = scrollTop;
      return;
    }

    if (this.hasActiveInteraction()) {
      this.setState('menu');
      this.resetTravel(scrollTop);
      this.lastScrollTop = scrollTop;
      return;
    }

    if (this.state === 'full') {
      this.setState('menu');
      this.resetTravel(scrollTop);
      this.lastScrollTop = scrollTop;
      return;
    }

    if (force) {
      this.setState('menu');
      this.resetTravel(scrollTop);
      this.lastScrollTop = scrollTop;
      return;
    }

    if (delta > 0) {
      this.upwardTravel = 0;
      this.header.dataset.scrollDirection = 'down';

      if (this.state === 'menu' && scrollTop - this.downwardStart >= this.config.hideDistance) {
        this.setState('hidden');
      }
    } else if (delta < 0) {
      this.downwardStart = scrollTop;
      this.upwardTravel += Math.abs(delta);
      this.header.dataset.scrollDirection = 'up';

      if (this.upwardTravel >= UPWARD_REVEAL_DISTANCE) {
        this.setState('menu');
      }
    }

    this.lastScrollTop = scrollTop;
  }

  hasActiveInteraction() {
    return Boolean(
      this.header.matches(':hover, :focus-within') ||
        this.header.querySelector(
          "header-menu[aria-expanded='true'], .menu-list__link[aria-expanded='true'], .menu-list__link[data-animating]"
        )
    );
  }

  resetTravel(scrollTop) {
    this.downwardStart = scrollTop;
    this.upwardTravel = 0;
    this.header.dataset.scrollDirection = 'none';
  }

  setState(nextState) {
    if (this.state === nextState && this.header.dataset.ruffooSmartState === nextState) return;

    this.state = nextState;
    this.header.dataset.ruffooSmartState = nextState;
  }
}

function readConfig() {
  const configElements = document.querySelectorAll(CONFIG_SELECTOR);
  const configElement = configElements[configElements.length - 1];
  let config = {};

  if (configElement) {
    try {
      config = JSON.parse(configElement.textContent || '{}');
    } catch (error) {
      console.warn('Ruffoo smart header config could not be read.', error);
    }
  }

  return {
    enabled: config.enabled !== false,
    hideDistance: Math.min(320, Math.max(80, Number(config.hideDistance) || 160)),
    animationSpeed: Math.min(400, Math.max(100, Number(config.animationSpeed) || 200)),
  };
}

async function initializeSmartHeader() {
  const config = readConfig();
  window[CONTROLLER_KEY]?.destroy();

  if (!config.enabled) return;

  await customElements.whenDefined('header-component');
  const header = document.querySelector('#header-component');
  if (!header || header.getAttribute('sticky') !== 'always') return;

  const controller = new RuffooSmartHeaderController(header, config);
  window[CONTROLLER_KEY] = controller;
  controller.init();
}

initializeSmartHeader();

document.addEventListener('shopify:section:load', initializeSmartHeader);
document.addEventListener('shopify:section:unload', initializeSmartHeader);
